import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
} from "@mui/material";
import { toast } from "react-toastify";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import CustomDataGridPro from "../../CustomDataGridPro";
import RightDrawer from "../../RightDrawer";
import userStore from "../../../stores/UserStore";

const PendingUser = observer(() => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const rows = userStore.allUsers.filter((u) => u.active === false);

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5 },
    { field: "name", headerName: "Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1.5 },
    { field: "createdDate", headerName: "Created Date", flex: 1 },
    { field: "updatedDate", headerName: "Updated Date", flex: 1 },
    {
      field: "emailVerified",
      headerName: "Email Verified",
      type: "boolean",
      flex: 0.8,
    },
    {
      field: "active",
      headerName: "Active Status",
      type: "boolean",
      flex: 0.8,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation(); // Prevent row click from firing
                setSelectedUser(row); // 👈 This opens the drawer
              }}
              sx={{
                mr: 1,
                color: "#22C55E",
                "&:hover": {
                  backgroundColor: "#BBF7D0",
                },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.id, row.email);
              }}
              sx={{
                color: "#6B7280",
                "&:hover": {
                  backgroundColor: "#E5E7EB",
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const handleDrawerClose = () => setSelectedUser(null);

  const handleUpdate = async () => {
    try {
      const updated = await userStore.updateUser({
        id: selectedUser.id,
        emailVerified: selectedUser.emailVerified,
        active: selectedUser.active,
      });

      setSelectedUser(updated);

      await userStore.fetchAllUsers();

      toast.success("User updated successfully!");

      handleDrawerClose();
    } catch (err) {
      toast.error(err.message || "Failed to update user");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;

    try {
      await userStore.deleteUser(id);
      toast.success("User deleted!");

      if (selectedUser?.id === id) {
        handleDrawerClose();
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete user");
    }
  };

  return (
    <>
      <CustomDataGridPro
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        onRowClick={(params) => setSelectedUser(params.row)}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalCount={rows.length}
        isLoading={userStore.loading}
      />

      <RightDrawer
        open={!!selectedUser}
        onClose={handleDrawerClose}
        title={selectedUser?.name || "User Details"}
      >
        {selectedUser && (
          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              gap: 3,
              p: 2,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Box
                sx={{ border: `2px solid gray`, borderRadius: "50%", p: 1.5 }}
              >
                <PersonIcon sx={{ fontSize: 64 }} />
              </Box>
            </Box>
            <TextField
              label="Email"
              value={selectedUser.email}
              fullWidth
              onChange={(e) =>
                setSelectedUser((prev) => ({
                  ...prev,
                  email: e.target.value,
                }))
              }
            />

            <FormControl fullWidth>
              <InputLabel>Email Verified</InputLabel>
              <Select
                label="Email Verified"
                value={selectedUser.emailVerified ? "Yes" : "No"}
                onChange={(e) =>
                  setSelectedUser((prev) => ({
                    ...prev,
                    emailVerified: e.target.value === "Yes",
                  }))
                }
              >
                <MenuItem value="No">No</MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!selectedUser.emailVerified}>
              <InputLabel>Active</InputLabel>
              <Select
                label="Active"
                value={selectedUser.active ? "Yes" : "No"}
                onChange={(e) =>
                  setSelectedUser((prev) => ({
                    ...prev,
                    active: e.target.value === "Yes",
                  }))
                }
              >
                <MenuItem value="No">No</MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Created Date"
              value={selectedUser.createdDate || ""}
              fullWidth
              disabled
            />
            <TextField
              label="Updated Date"
              value={selectedUser.updatedDate || ""}
              fullWidth
              disabled
            />

            <Box
              sx={{
                mt: "auto",
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                pt: 2,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Button
                variant="contained"
                onClick={handleDrawerClose}
                className="px-6 py-2 rounded-lg !normal-case"
                sx={{
                  backgroundColor: "transparent",
                  color: "#6B7280",
                  border: "1px solid #6B7280",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleUpdate}
                className="px-6 py-2 rounded-lg !normal-case"
                sx={{
                  backgroundColor: "#22C55E",
                  color: "#FFFFFF",
                }}
              >
                Update
              </Button>
            </Box>
          </Box>
        )}
      </RightDrawer>
    </>
  );
});

export default PendingUser;
